import React, { useMemo } from "react"

import { Button } from "@mantine/core"

import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/index.module.css"
import { Icon, renderFieldError } from "@/components"
import { QuestionItem } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionItem"
import { FieldError, useFormContext } from "react-hook-form"
import { CustomField } from "@/types/djangio"
import { PERSONALIZED_PAGES_KEY } from "@/pages/campaign-form/components/campaigns/personalizedMessages/Messaging"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { CampaignFormValues } from "@/pages/campaign-form/types"

interface Props {
    onPlusClick: () => void
    onTaskClick: () => void
}

export const CustomQuestions = (props: Props) => {
    const formContext = useFormContext<CampaignFormValues>()

    const personalizedPagesError = formContext.formState.errors[PERSONALIZED_PAGES_KEY] as unknown as FieldError

    const personalizedPages = formContext.watch(PERSONALIZED_PAGES_KEY)
    const parsedPersonalizedPages = useMemo(() => {
        return Object.keys(personalizedPages).reduce((acc, key) => {
            if (personalizedPages[key].length > 0) {
                acc.push(personalizedPages[key])
            }
            return acc
        }, [])
    }, [personalizedPages]) as CustomField[][]

    const onDragEnd = (event) => {
        const startIndex = event.source.index
        const endIndex = event.destination.index
        const endQuestion = { pageIndex: 0, questionIndex: 0 }
        let cumulative_index = -1
        const newPages = {}
        const draggedItem = parsedPersonalizedPages.flat()[startIndex]

        parsedPersonalizedPages.forEach((page, index) => {
            if (!newPages[index]) newPages[index] = []
            page.forEach((question, questionIndex) => {
                cumulative_index++
                const isStart = cumulative_index === startIndex
                const isEnd = cumulative_index === endIndex
                if (isStart) return
                if (isEnd) {
                    endQuestion["pageIndex"] = index
                    endQuestion["questionIndex"] = questionIndex
                }
                newPages[index].push(question)
            })
        })
        newPages[endQuestion.pageIndex].splice(endQuestion.questionIndex, 0, draggedItem)

        formContext.setValue(PERSONALIZED_PAGES_KEY, newPages)
    }

    return (
        <div className={classes.box}>
            <div className={classes.topbar}>
                <div className={classes.title}>Custom questions</div>
                <div className={classes.buttonsContainer}>
                    <Button
                        variant="subtle"
                        className={classes.button}
                        onClick={props.onPlusClick}
                        data-cy="add-new-question-button"
                        aria-label="Add new question"
                    >
                        <Icon icon="plus" />
                    </Button>
                    <Button
                        variant="subtle"
                        className={classes.button}
                        onClick={props.onTaskClick}
                        aria-label="Open saved questions"
                    >
                        <Icon icon="tasks" />
                    </Button>
                </div>
            </div>
            {personalizedPagesError && renderFieldError(personalizedPagesError)}
            {parsedPersonalizedPages.length ? (
                <DragDropContext onDragEnd={(event) => onDragEnd(event)}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div
                                className={classes.customQuestions}
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {parsedPersonalizedPages.map((questions, pageIndex) => (
                                    <div className={classes.page} key={pageIndex}>
                                        <div className={classes.pageNumber}>PAGE {pageIndex + 1}</div>
                                        {questions.map((question: CustomField, index: number) => (
                                            <Draggable
                                                key={question.id}
                                                draggableId={String(question.id)}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <QuestionItem
                                                        provided={provided}
                                                        key={question.id}
                                                        question={question}
                                                        onDelete={() => {
                                                            const newPages = parsedPersonalizedPages.reduce(
                                                                (acc, page, reducingIndex) => {
                                                                    if (reducingIndex !== pageIndex)
                                                                        acc[reducingIndex] = page
                                                                    else {
                                                                        const questions = page.filter(
                                                                            (q) => q.id !== question.id,
                                                                        )
                                                                        if (questions.length === 0) return acc
                                                                        else acc[reducingIndex] = questions
                                                                    }
                                                                    return acc
                                                                },
                                                                {},
                                                            )
                                                            formContext.setValue(PERSONALIZED_PAGES_KEY, newPages)
                                                        }}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                    </div>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            ) : (
                <EmptyState />
            )}
        </div>
    )
}

const EmptyState = () => (
    <div className={classes.page}>
        <div className={classes.pageNumber}>PAGE 1</div>
        <span className={classes.noQuestionsAdded}>No questions added. Click on the plus button to add.</span>
    </div>
)
